import { Alert, Box, Button, Link } from "@mui/material";
import { Link as RouterLink } from "react-router";
import dayjs from "dayjs";
import { BuilderCard, CardHeading } from "./builderUi";
import { candidateAmount, respondByLeft } from "../../utils/bankCaseUtils";
import { EVIDENCE_STATUS_LABELS } from "../../types/evidence";
import { BankCaseListRow } from "../../types/evidenceBankCase";

export default function RecentBankCases({ cases, hasMore, loading, error, showCompany, onMore }: {
  cases: BankCaseListRow[];
  hasMore: boolean;
  loading: boolean;
  error: string | null;
  /** No company is picked, so the list spans all of them. */
  showCompany: boolean;
  onMore: () => void;
}) {
  const row = {
    display: "grid", gap: "12px", px: "4px",
    // minmax(0, …) keeps every row on the same columns; a long dispute ID wraps instead of widening its own row.
    gridTemplateColumns: showCompany
      ? "minmax(0,1.1fr) minmax(0,1.2fr) minmax(0,1.2fr) minmax(0,1.4fr) minmax(0,1fr) minmax(0,1fr) minmax(0,0.8fr)"
      : "minmax(0,1.1fr) minmax(0,1.2fr) minmax(0,1.4fr) minmax(0,1fr) minmax(0,1fr) minmax(0,0.8fr)",
  };
  return (
    <BuilderCard sx={{ gap: "10px" }}>
      <CardHeading>Recent bank cases</CardHeading>
      {error && <Alert severity="error">{error}</Alert>}
      {!error && cases.length === 0 && !loading && <Box sx={{ color: "#667085" }}>No bank cases yet.</Box>}
      {cases.length > 0 && (
        <Box role="table" aria-label="Recent bank cases" sx={{ display: "flex", flexDirection: "column", gap: "10px", overflowX: "auto" }}>
          <Box role="row" sx={{ ...row, color: "#667085", fontSize: 12 }}>
            <Box role="columnheader">Dispute ID</Box>
            {showCompany && <Box role="columnheader">Company</Box>}
            <Box role="columnheader">Bank</Box>
            <Box role="columnheader">Order</Box>
            <Box role="columnheader">Amount</Box>
            <Box role="columnheader">Respond by</Box>
            <Box role="columnheader">Evidence</Box>
          </Box>
          {cases.map((bankCase) => {
            const due = bankCase.respond_by ? respondByLeft(bankCase.respond_by) : null;
            return (
              <Box role="row" key={bankCase.evidence_bank_case_id} sx={{ ...row, alignItems: "center", borderTop: "1px solid #f2f4f7", py: "10px" }}>
                <Box role="cell" sx={{ overflowWrap: "anywhere" }}>
                  <Link component={RouterLink} to={`/evidence-builder/${bankCase.evidence_bank_case_id}`} underline="none" sx={{ fontWeight: 600 }}>
                    {bankCase.dispute_reference}
                  </Link>
                </Box>
                {showCompany && <Box role="cell">{bankCase.company_name}</Box>}
                <Box role="cell">{bankCase.bank_name ?? "--"}</Box>
                <Box role="cell">{bankCase.order_name}</Box>
                <Box role="cell">{candidateAmount(bankCase.amount, bankCase.currency)}</Box>
                <Box role="cell" sx={due?.urgent ? { color: "#b42318", fontWeight: 600 } : undefined}>
                  {bankCase.respond_by && due ? `${dayjs.utc(bankCase.respond_by).format("MMM D")} · ${due.left}` : "--"}
                </Box>
                <Box role="cell" sx={bankCase.draft_status === "generated" ? { color: "#067647", fontWeight: 600 } : { color: "#475467" }}>
                  {bankCase.draft_status ? EVIDENCE_STATUS_LABELS[bankCase.draft_status] : "No draft"}
                </Box>
              </Box>
            );
          })}
        </Box>
      )}
      {hasMore && (
        <Box>
          <Button size="small" onClick={onMore} disabled={loading}>Show more</Button>
        </Box>
      )}
    </BuilderCard>
  );
}
